import { GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";

/**
 * @param {import('firebase/auth').Auth} fbAuth The firebase auth object
 * @returns {object} Auth functions
 */
export default function authnFns(fbAuth) {
  /**
   * Initiates Google sign-in popup flow.
   * @returns {Promise<import('firebase/auth').User>} Promise resolving to the signed-in user
   * @throws {Error} If sign-in fails
   */
  async function signInWithGoogle() {
    const googleProvider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(fbAuth, googleProvider);
      return result.user;
    } catch (error) {
      console.error(error.code);
      throw error;
    }
  }

  /**
   * Signs out the current user.
   * @returns {Promise<void>}
   */
  async function signout() {
    await signOut(fbAuth);
  }

  /**
   * Gets the currently signed-in user.
   * @returns {import('firebase/auth').User | null} The current user or null if not signed in
   */
  function getCurrentUser() {
    return fbAuth.currentUser;
  }

  /**
   * Sets up an observer for authentication state changes.
   * @param {(user: import('firebase/auth').User | null) => void} callback Function called when auth state changes
   * @returns {() => void} Unsubscribe function
   */
  function onAuthStateChanged(callback) {
    return fbAuth.onAuthStateChanged(callback);
  }

  /**
   * Forces a token refresh to get updated custom claims.
   * Call this after role changes to immediately reflect permissions.
   * @param {import('firebase/auth').User} user Firebase auth user
   * @returns {Promise<void>}
   */
  async function refreshUserToken(user) {
    if (!user) {
      return;
    }

    try {
      await user.getIdToken(true);
    } catch (error) {
      console.error("Failed to refresh user token:", error);
      throw error;
    }
  }

  /**
   * Waits for custom claims to be set on the user token.
   * Polls the token with exponential backoff until claims exist.
   * @param {import('firebase/auth').User} user Firebase auth user
   * @param {number} maxAttempts Maximum number of retry attempts
   * @returns {Promise<boolean>} True if claims found, false if timeout
   */
  async function waitForClaims(user, maxAttempts = 10) {
    if (!user) {
      return false;
    }

    for (let i = 0; i < maxAttempts; i++) {
      try {
        const tokenResult = await user.getIdTokenResult(true);
        console.log(tokenResult.claims);
        // Check if custom claims exist (orgs or teams)
        if (tokenResult.claims.orgs || tokenResult.claims.teams) {
          return true;
        }

        // Wait with exponential backoff: 100ms, 200ms, 400ms, 800ms, etc.
        const delay = Math.min(100 * Math.pow(2, i), 2000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      } catch {}
    }

    return false;
  }

  return {
    signInWithGoogle,
    signout,
    getCurrentUser,
    onAuthStateChanged,
    refreshUserToken,
    waitForClaims,
  };
}
